import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { LEDGER_KIND_LABELS, type LeaveBalanceDto, type LedgerEntryDto } from '@workforce/shared';
import { api } from '@/lib/api';
import { useTz } from '@/lib/timezone';
import { t } from '@/lib/i18n';
import { BalanceCards } from '@/components/BalanceCards';
import { Card, EmptyState, ErrorBanner, PageHeader, Select, Spinner } from '@/components/ui';

export function LedgerTable({ entries }: { entries: LedgerEntryDto[] }) {
  const { fmt } = useTz();
  if (!entries.length) return <EmptyState title={t('No movements yet')} />;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-100">
        <thead className="bg-slate-50">
          <tr>
            <th className="th">{t('Date')}</th>
            <th className="th">{t('Movement')}</th>
            <th className="th text-end">{t('Days')}</th>
            <th className="th">{t('Details')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="td">{fmt(e.createdAt, null, 'dd LLL yyyy')}</td>
              <td className="td">{t(LEDGER_KIND_LABELS[e.kind])}</td>
              <td className={`td text-end font-medium tabular-nums ${e.delta < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                {e.delta > 0 ? '+' : ''}
                {e.delta}
              </td>
              <td className="td whitespace-normal text-xs text-slate-500">
                {e.reason ? t(e.reason) : ''}
                {e.createdByLabel && e.createdByLabel !== 'system' ? ` · ${t('by {name}', { name: e.createdByLabel })}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MyBalancePage() {
  const thisYear = DateTime.now().year;
  const [year, setYear] = useState(thisYear);
  const [selected, setSelected] = useState<LeaveBalanceDto | null>(null);
  const date = year === thisYear ? DateTime.now().toISODate()! : `${year}-06-30`;
  const balances = useQuery({ queryKey: ['me', 'leave', 'balances', date], queryFn: () => api.get<LeaveBalanceDto[]>(`/me/leave/balances?date=${date}`) });
  const current = selected ? balances.data?.find((b) => b.leaveTypeId === selected.leaveTypeId) ?? null : balances.data?.find((b) => b.isPrimary && b.balanceId) ?? null;
  const ledger = useQuery({
    queryKey: ['me', 'leave', 'ledger', current?.balanceId],
    queryFn: () => api.get<LedgerEntryDto[]>(`/me/leave/balances/${current!.balanceId}/ledger`),
    enabled: !!current?.balanceId,
  });
  return (
    <div>
      <PageHeader
        title={t('My balance')}
        subtitle={t('Every change to your balance is listed — nothing is changed without a record.')}
        actions={
          <Select value={year} onChange={(e) => { setYear(Number(e.target.value)); setSelected(null); }} className="w-28" aria-label={t('Year')}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        }
      />
      <ErrorBanner error={balances.error} />
      {balances.isLoading ? <Spinner /> : <BalanceCards balances={balances.data ?? []} onSelect={setSelected} selectedId={current?.leaveTypeId} />}
      <Card className="mt-6" title={current ? t('{type} — history', { type: t(current.leaveTypeName) }) : t('History')} bodyClass="p-0">
        {!current?.balanceId ? (
          <EmptyState title={t('No movements yet')} description={t('Select a leave type above. History starts with your first request or adjustment in this leave year.')} />
        ) : ledger.isLoading ? (
          <Spinner />
        ) : (
          <LedgerTable entries={ledger.data ?? []} />
        )}
      </Card>
    </div>
  );
}
